"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Briefcase, ArrowLeft, RefreshCw, Layers } from "lucide-react";

interface HiringTopBarProps {
  currency?: "INR" | "USD";
  onCurrencyToggle?: (c: "INR" | "USD") => void;
  onResetDemo?: () => void;
  isResetting?: boolean;
}

export function HiringTopBar({
  currency = "INR",
  onCurrencyToggle,
  onResetDemo,
  isResetting = false,
}: HiringTopBarProps) {
  const pathname = usePathname();
  const isCrm = pathname?.includes("/crm");

  return (
    <header className="sticky top-0 z-50 w-full bg-[#0d0914]/80 backdrop-blur-md border-b border-purple-950/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Left: Brand & Back to Hub */}
        <div className="flex items-center gap-4">
          <Link
            href="/demos"
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">All Demos</span>
          </Link>
          <div className="h-4 w-px bg-purple-900/40" />
          <Link href="/demos/hiring" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/20 group-hover:scale-105 transition-transform">
              <Briefcase className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-white tracking-tight text-sm sm:text-base">Nexus Search</span>
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 font-semibold border border-purple-500/20">
                  Executive Talent
                </span>
              </div>
              <p className="text-[10px] text-slate-400 hidden sm:block">
                C-Suite • Tech Leadership • Recruitment Pipeline CRM
              </p>
            </div>
          </Link>
        </div>

        {/* Center / Navigation Switcher */}
        <div className="flex items-center bg-[#181126] border border-purple-900/40 p-1 rounded-xl">
          <Link
            href="/demos/hiring"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              !isCrm
                ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow"
                : "text-slate-400 hover:text-white"
            }`}
          >
            Client Portal
          </Link>
          <Link
            href="/demos/hiring/crm"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
              isCrm
                ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Recruitment CRM</span>
          </Link>
        </div>

        {/* Right: Currency Toggle & Reset Demo */}
        <div className="flex items-center gap-2 sm:gap-3">
          {onCurrencyToggle && (
            <div className="flex items-center bg-[#181126] border border-purple-900/40 p-0.5 rounded-lg text-xs font-semibold">
              <button
                type="button"
                onClick={() => onCurrencyToggle("INR")}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  currency === "INR"
                    ? "bg-purple-600 text-white"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                ₹ INR
              </button>
              <button
                type="button"
                onClick={() => onCurrencyToggle("USD")}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  currency === "USD"
                    ? "bg-purple-600 text-white"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                $ USD
              </button>
            </div>
          )}

          {onResetDemo && (
            <button
              type="button"
              onClick={onResetDemo}
              disabled={isResetting}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white bg-[#181126] hover:bg-[#231838] border border-purple-900/40 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              title="Reset candidates to initial demo state"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isResetting ? "animate-spin text-purple-400" : ""}`} />
              <span className="hidden md:inline">Reset Demo</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
